import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import LoadingOverlay from '../components/LoadingOverlay';
import './AppPage.css';
import './LeavesPage.css';
const states: any = {
  DRAFT: 'Borrador',
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Cancelada',
};
const periodStates: any = { UPCOMING: 'Próxima', ACTIVE: 'Activa', FINISHED: 'Finalizada' };
export default function LeavesPage() {
  const nav = useNavigate(),
    [items, setItems] = useState<any[]>([]),
    [types, setTypes] = useState<any[]>([]),
    [employees, setEmployees] = useState<any[]>([]),
    [dash, setDash] = useState<any>({ currentlyAbsent: [], upcoming: [], returns: [] }),
    [filters, setFilters] = useState<any>({
      employeeId: '',
      leaveTypeId: '',
      status: '',
      periodStatus: '',
      from: '',
      to: '',
    }),
    [form, setForm] = useState<any>({
      employeeId: '',
      leaveTypeId: '',
      startAt: '',
      endAt: '',
      reason: '',
      internalNotes: '',
      status: 'APPROVED',
    }),
    [show, setShow] = useState(false),
    [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(''),
    [error, setError] = useState('');
  const load = async () => {
    try {
      const [i, t, e, d] = await Promise.all([
        apiClient.get('/leaves', {
          params: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
        }),
        apiClient.get('/leaves/types'),
        apiClient.get('/employees'),
        apiClient.get('/leaves/dashboard'),
      ]);
      setItems(i.data);
      setTypes(t.data);
      setEmployees(e.data);
      setDash(d.data);
      setForm((x: any) => ({
        ...x,
        employeeId: x.employeeId || e.data[0]?.id || '',
        leaveTypeId: x.leaveTypeId || t.data[0]?.id || '',
      }));
    } catch {
      setError('No se pudo cargar el módulo de licencias.');
    }
  };
  useEffect(() => {
    void load();
  }, [
    filters.employeeId,
    filters.leaveTypeId,
    filters.status,
    filters.periodStatus,
    filters.from,
    filters.to,
  ]);
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('Guardando licencia…');
    try {
      const created = await apiClient.post('/leaves', {
        ...form,
        startAt: new Date(form.startAt).toISOString(),
        endAt: new Date(form.endAt).toISOString(),
      });
      if (file) {
        const data = new FormData();
        data.append('file', file);
        await apiClient.post(`/leaves/${created.data.id}/attachment`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setFile(null);
      }
      setShow(false);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'No se pudo guardar.');
    } finally {
      setBusy('');
    }
  };
  const resolve = async (x: any, status: string) => {
    const rejectionReason = status === 'REJECTED' ? prompt('Motivo del rechazo:') : undefined;
    if (status === 'REJECTED' && !rejectionReason) return;
    setBusy('Resolviendo solicitud…');
    try {
      await apiClient.patch(`/leaves/${x.id}/resolve`, { status, rejectionReason });
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'No se pudo resolver.');
    } finally {
      setBusy('');
    }
  };
  const cancel = async (x: any) => {
    if (!confirm('¿Cancelar esta licencia?')) return;
    setBusy('Cancelando…');
    try {
      await apiClient.patch(`/leaves/${x.id}/cancel`, {});
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'No se pudo cancelar.');
    } finally {
      setBusy('');
    }
  };
  const addType = async () => {
    const name = prompt('Nombre del nuevo tipo de licencia:');
    if (!name) return;
    await apiClient.post('/leaves/types', { name });
    await load();
  };
  const balance = async (e: any) => {
    const r = await apiClient.get(`/leaves/vacation/${e.id}`),
      days = prompt(
        `Vacaciones: ${r.data.used} usados, ${r.data.available} disponibles. Días correspondientes:`,
        String(r.data.entitled)
      );
    if (days)
      await apiClient.post('/leaves/vacation/entitlement', {
        employeeId: e.id,
        year: new Date().getFullYear(),
        entitledDays: Number(days),
      });
  };
  const openAttachment = async (documentId: string) => {
    setBusy('Abriendo adjunto…');
    try {
      const response = await apiClient.get(`/employee-documents/${documentId}/file`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError('No se pudo abrir el archivo adjunto.');
    } finally {
      setBusy('');
    }
  };
  return (
    <div className="app-page">
      {busy && <LoadingOverlay title={busy} />}
      <header className="app-bar">
        <button className="back-button" onClick={() => nav('/dashboard')}>
          ← Volver
        </button>
        <h1>Licencias y ausencias</h1>
        <button className="primary-button" onClick={() => setShow(true)}>
          Nueva licencia
        </button>
      </header>
      <main className="app-main">
        {error && <div className="error-banner">{error}</div>}
        <div className="leave-stats">
          <span>
            <strong>{dash.currentlyAbsent.length}</strong>Ausentes ahora
          </span>
          <span>
            <strong>{dash.upcoming.length}</strong>Próximas
          </span>
          <span>
            <strong>{dash.returns.length}</strong>Reincorporaciones
          </span>
        </div>
        {show && (
          <section className="panel">
            <form className="leave-form" onSubmit={create}>
              <label>
                Empleado
                <select
                  required
                  value={form.employeeId}
                  onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                >
                  {employees.map((x) => (
                    <option value={x.id}>
                      {x.firstName} {x.lastName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Tipo
                <select
                  required
                  value={form.leaveTypeId}
                  onChange={(e) => setForm({ ...form, leaveTypeId: e.target.value })}
                >
                  {types.map((x) => (
                    <option value={x.id}>{x.name}</option>
                  ))}
                </select>
              </label>
              <label>
                Inicio
                <input
                  required
                  type="datetime-local"
                  value={form.startAt}
                  onChange={(e) => setForm({ ...form, startAt: e.target.value })}
                />
              </label>
              <label>
                Finalización
                <input
                  required
                  type="datetime-local"
                  value={form.endAt}
                  onChange={(e) => setForm({ ...form, endAt: e.target.value })}
                />
              </label>
              <label>
                Estado
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="APPROVED">Aprobada</option>
                  <option value="PENDING">Pendiente</option>
                  <option value="DRAFT">Borrador</option>
                </select>
              </label>
              <label>
                Motivo
                <input
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                />
              </label>
              <label className="wide">
                Observaciones internas
                <textarea
                  value={form.internalNotes}
                  onChange={(e) => setForm({ ...form, internalNotes: e.target.value })}
                />
              </label>
              <label className="wide">
                Archivo adjunto (PDF, JPG o PNG)
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
              <div className="wide row-actions">
                <button type="button" className="secondary-button" onClick={() => setShow(false)}>
                  Cancelar
                </button>
                <button className="primary-button">Guardar</button>
              </div>
            </form>
          </section>
        )}
        <section className="panel">
          <div className="panel-heading">
            <h2>Calendario e historial</h2>
            <button className="secondary-button" onClick={() => void addType()}>
              Agregar tipo
            </button>
          </div>
          <div className="leave-filters">
            <select
              value={filters.employeeId}
              onChange={(e) => setFilters({ ...filters, employeeId: e.target.value })}
            >
              <option value="">Todos los empleados</option>
              {employees.map((x) => (
                <option value={x.id}>
                  {x.firstName} {x.lastName}
                </option>
              ))}
            </select>
            <select
              value={filters.leaveTypeId}
              onChange={(e) => setFilters({ ...filters, leaveTypeId: e.target.value })}
            >
              <option value="">Todos los tipos</option>
              {types.map((x) => (
                <option value={x.id}>{x.name}</option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">Todos los estados</option>
              {Object.entries(states).map(([v, n]) => (
                <option value={v}>{n as string}</option>
              ))}
            </select>
            <select
              value={filters.periodStatus}
              onChange={(e) => setFilters({ ...filters, periodStatus: e.target.value })}
            >
              <option value="">Todas las situaciones</option>
              <option value="ACTIVE">Activas</option>
              <option value="UPCOMING">Próximas</option>
              <option value="FINISHED">Finalizadas</option>
            </select>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            />
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            />
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>Tipo</th>
                  <th>Período</th>
                  <th>Cantidad</th>
                  <th>Estado</th>
                  <th>Situación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((x) => (
                  <tr key={x.id}>
                    <td>
                      {x.employee.firstName} {x.employee.lastName}
                    </td>
                    <td>{x.leaveType.name}</td>
                    <td>
                      {new Date(x.startAt).toLocaleString('es-AR')}
                      <br />
                      {new Date(x.endAt).toLocaleString('es-AR')}
                    </td>
                    <td>
                      {x.quantity} {x.quantityUnit === 'DAYS' ? 'días' : 'horas'}
                    </td>
                    <td>{states[x.status]}</td>
                    <td>
                      <span className={`period-badge ${String(x.periodStatus).toLowerCase()}`}>
                        {periodStates[x.periodStatus]}
                      </span>
                    </td>
                    <td className="row-actions">
                      {x.status === 'PENDING' && (
                        <>
                          <button
                            className="primary-button"
                            onClick={() => void resolve(x, 'APPROVED')}
                          >
                            Aprobar
                          </button>
                          <button
                            className="danger-button"
                            onClick={() => void resolve(x, 'REJECTED')}
                          >
                            Rechazar
                          </button>
                        </>
                      )}
                      {['DRAFT', 'PENDING', 'APPROVED'].includes(x.status) &&
                        x.periodStatus !== 'FINISHED' && (
                          <button className="secondary-button" onClick={() => void cancel(x)}>
                            Cancelar
                          </button>
                        )}
                      {x.attachmentDocumentId && (
                        <button
                          className="secondary-button"
                          onClick={() => void openAttachment(x.attachmentDocumentId)}
                        >
                          Ver adjunto
                        </button>
                      )}
                      {x.leaveType.name === 'Vacaciones' && (
                        <button
                          className="secondary-button"
                          onClick={() => void balance(x.employee)}
                        >
                          Ver/configurar saldo
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
